/**
 * How a Kraken limb physically seizes the rig — one description, evaluated by the mesh
 * AND the gun.
 *
 * The previous rig grip wound each limb around a jacket leg, which was anatomically
 * lovely from an exterior camera and invisible from the only camera the player has: the
 * legs stand 28 m from the origin, directly under the deck plate, and the stance cannot
 * look down past 25 degrees. A limb the player must sever (Adam: "you must make sure
 * that the player can see and shoot the limbs") cannot live below their feet.
 *
 * This wrap rears instead. The limb roots in open water off the platform, comes up
 * through the surface, and arcs onto the deck edge itself — so its upper half stands
 * against the sky in the exact pitch band the gun can reach, and the tear beat is a hook
 * jabbing down onto the rim the player is standing on.
 *
 * Pure math in content/ because the simulation collides against exactly this path: the
 * limb's hit spheres ride the same curve, so it is shootable where it is drawn.
 */

import type { Vessel } from '../sim/types';

export interface KrakenRigGrip {
  /** How completely the limb has reached the rig, 0..1. */
  grip: number;
  /** Short local snap at the end of a long loading beat, 0..1. */
  slam: number;
  /** Slow muscular contraction shared by the path and the skin pulse. */
  clutch: number;
  /** Limb origin and yaw, retained so the authored path can be returned in local space. */
  originX: number;
  originY: number;
  originZ: number;
  heading: number;
  /** Retained for the water FX: which quadrant the limb loads. */
  legSignX: -1 | 1;
  legSignZ: -1 | 1;
  /** Where the rising limb breaks the surface — the boil lives here now, not at a leg. */
  legWaterX: number;
  legWaterZ: number;
  /** Direction of the lateral bow and stable anatomical variation. */
  coilSign: -1 | 1;
  variant: number;
  approachAngle: number;
}

export interface KrakenRigPoint {
  x: number;
  y: number;
  z: number;
}

/** Where the hook lands: the deck edge, on the limb's own side of the platform. */
const RIM_RADIUS = 40;
const RIM_Y = 100;
/** Grip develops as the limb closes on the platform; measured to the origin. */
const GRIP_START_RANGE = 200;
const GRIP_FULL_RANGE = 150;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function positiveModulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

/**
 * Resolve a rig grip only for an unassigned, live attack limb close enough to reach.
 *
 * A limb with a vessel target is handled by krakenPredation instead. This separation is
 * important: faction-blind ship predation and the rig fail state remain two presentations
 * of two real simulation outcomes, never competing visual guesses.
 */
export function krakenRigGrip(
  tentacle: Vessel,
  simTime: number,
): KrakenRigGrip | null {
  if (
    tentacle.kind !== 'tentacle'
    || tentacle.targetId !== null
    || tentacle.state === 'dead'
    || tentacle.state === 'sinking'
    || tentacle.state === 'departing'
  ) {
    return null;
  }

  const range = Math.hypot(tentacle.pos.x, tentacle.pos.z);
  const grip = smoothstep(
    (GRIP_START_RANGE - range) / (GRIP_START_RANGE - GRIP_FULL_RANGE),
  );
  if (grip <= 0) return null;

  const legSignX: -1 | 1 = tentacle.pos.x < 0 ? -1 : 1;
  const legSignZ: -1 | 1 = tentacle.pos.z < 0 ? -1 : 1;
  // Direction from the origin out toward the limb: the hook lands on the rim along it,
  // and the breach boil sits between the root and that rim.
  const out = Math.max(1, range);
  const ux = tentacle.pos.x / out;
  const uz = tentacle.pos.z / out;
  const approachAngle = Math.atan2(uz, ux);
  const coilSign: -1 | 1 = (tentacle.id + (legSignX === legSignZ ? 1 : 0)) % 2 === 0
    ? 1
    : -1;
  // A small integer hash gives neighbouring entity ids very different anatomies. A
  // simple id modulo made the first three spawned limbs all choose nearly identical
  // heights and turns, producing an accidental trident around the jacket.
  const variant = ((tentacle.id * tentacle.id * 37 + tentacle.id * 17) % 23) / 11 - 1;

  // A 6.4 s load gives the mass time to read. Only the last fifth is a quick tear, so
  // the limb has enormous inertia everywhere except the actual contact beat.
  const cycle = positiveModulo(simTime + tentacle.id * 1.91, 6.4) / 6.4;
  const slamWindow = clamp01((cycle - 0.78) / 0.22);
  const slam = Math.sin(slamWindow * Math.PI) * smoothstep(grip);
  const clutch = 0.82
    + Math.sin(simTime * 0.31 + tentacle.id * 1.27) * 0.13
    + slam * 0.11;

  // The breach point: where the rising limb crosses the surface on its way to the rim.
  const rimX = ux * RIM_RADIUS;
  const rimZ = uz * RIM_RADIUS;

  return {
    grip,
    slam,
    clutch,
    originX: tentacle.pos.x,
    originY: tentacle.pos.y,
    originZ: tentacle.pos.z,
    heading: tentacle.heading,
    legSignX,
    legSignZ,
    legWaterX: tentacle.pos.x + (rimX - tentacle.pos.x) * 0.22,
    legWaterZ: tentacle.pos.z + (rimZ - tentacle.pos.z) * 0.22,
    coilSign,
    variant,
    approachAngle,
  };
}

function worldToLocal(
  grip: KrakenRigGrip,
  worldX: number,
  worldY: number,
  worldZ: number,
): KrakenRigPoint {
  const dx = worldX - grip.originX;
  const dz = worldZ - grip.originZ;
  const c = Math.cos(grip.heading);
  const s = Math.sin(grip.heading);
  return {
    x: c * dx - s * dz,
    y: worldY - grip.originY,
    z: s * dx + c * dz,
  };
}

function cubic(
  a: number,
  b: number,
  c: number,
  d: number,
  t: number,
): number {
  const s = 1 - t;
  return a * s * s * s + b * 3 * s * s * t + c * 3 * s * t * t + d * t * t * t;
}

/**
 * Continuous centreline from the planted water root, up through the surface, to a hook
 * over the deck edge.
 *
 * Three beats: an underwater shoulder to the breach point, a long muscular rear through
 * open air toward a crest outside the rim, and the hook itself — over the rim, curling
 * inboard and down, with the tear jab driving the tip onto the deck. The middle beat is
 * the shootable one: it stands 40–90 m up at 40–80 m out, which from a mount 105 m up is
 * near-level aim.
 */
export function krakenRigWrapPoint(
  grip: KrakenRigGrip,
  progress: number,
): KrakenRigPoint {
  const t = clamp01(progress);
  if (t === 0) return { x: 0, y: -30, z: 0 };

  const ux = Math.cos(grip.approachAngle);
  const uz = Math.sin(grip.approachAngle);
  const rimX = ux * RIM_RADIUS;
  const rimZ = uz * RIM_RADIUS;
  const rimY = RIM_Y + 2 + grip.variant * 3;
  // Lateral bow direction — perpendicular to the reach, giving each limb its own lean.
  const sideX = -uz * grip.coilSign;
  const sideZ = ux * grip.coilSign;

  const breachX = grip.legWaterX;
  const breachZ = grip.legWaterZ;

  /*
   * The crest stands near deck height and near the limb's OWN root.
   *
   * Both numbers came off captures. Height: the player's pitch clamp (-25°) hides
   * anything below ~44 m at these ranges, so a 74 m crest put most of the reared limb
   * under their chin. Position: an early staging carried the crest 62% of the way to
   * the rim, which bowed the whole upper body forward and every limb read as a drooping
   * hooded bulb — the neck must stand upright and leave the reaching to the hook.
   */
  const crestBow = (7 + grip.variant * 4) * grip.clutch;
  const crestX = grip.originX + (rimX - grip.originX) * 0.40 + sideX * crestBow;
  const crestZ = grip.originZ + (rimZ - grip.originZ) * 0.40 + sideZ * crestBow;
  const crestY = 92 + grip.variant * 8 + grip.slam * 5;

  if (t < 0.30) {
    // Underwater shoulder: root to the breach, bowing with the contraction.
    const q = smoothstep(t / 0.30);
    const bow = (10 + grip.variant * 5) * grip.clutch;
    return worldToLocal(
      grip,
      cubic(grip.originX, grip.originX + sideX * bow, breachX + sideX * bow * 0.4, breachX, q),
      cubic(grip.originY - 30, grip.originY - 22, -6, 2, q),
      cubic(grip.originZ, grip.originZ + sideZ * bow, breachZ + sideZ * bow * 0.4, breachZ, q),
    );
  }

  if (t < 0.66) {
    // The rear: breach to crest, climbing near-vertically into the band the gun can
    // actually reach. The neck is the target; it has to stand, not slump.
    const q = smoothstep((t - 0.30) / 0.36);
    return worldToLocal(
      grip,
      cubic(breachX, breachX + sideX * crestBow * 0.7, crestX, crestX, q),
      cubic(2, 34 + grip.variant * 6, crestY - 8, crestY, q),
      cubic(breachZ, breachZ + sideZ * crestBow * 0.7, crestZ, crestZ, q),
    );
  }

  // The hook: a long horizontal reach at the top, over the rim, then the curl down.
  // This is the arm laid across the gap to the deck edge; the tear jab drives the tip
  // onto the rim for the beat the integrity cost belongs to.
  const q = smoothstep((t - 0.66) / 0.34);
  const jab = Math.pow(q, 2.6) * grip.slam;
  const tipX = rimX - ux * 9;
  const tipZ = rimZ - uz * 9;
  const tipY = rimY - 5 - jab * 9;
  return worldToLocal(
    grip,
    cubic(crestX, crestX + (rimX - crestX) * 0.45, rimX + sideX * 3, tipX, q),
    cubic(crestY, crestY + 11 + grip.variant * 3, rimY + 6, tipY, q),
    cubic(crestZ, crestZ + (rimZ - crestZ) * 0.45, rimZ + sideZ * 3, tipZ, q),
  );
}
